import React, { useState, useEffect } from 'react';
import {
  Typography,
  Card,
  Table,
  Button,
  Space,
  Tag,
  message,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Popconfirm,
  Upload,
  Image,
  Checkbox,
  Radio,
  Row,
  Col,
  Cascader,
  Descriptions,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  KeyOutlined,
  SearchOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import type { UploadProps } from 'antd';
import { useLocation } from 'react-router-dom';
import { userService, UserListItem, CreateUserRequest, UpdateUserRequest, UserProviderSettlementSummary } from '../services/userService';
import { roleService, Role } from '../services/roleService';
import { getAllServiceCategories, type ServiceCategory } from '../services/serviceCategoryService';
import { listMeituanShops, type MeituanShop } from '../services/meituanShopService';
import { normalizeFileUrl, uploadFile } from '../services/fileService';
import { getRegionTree, type RegionItem } from '../services/regionService';

const { Title } = Typography;
const { Option } = Select;

type WechatServiceRegionValue = {
  regionId?: string;
  provinceName?: string;
  cityName?: string;
  districtName?: string;
};

const convertRegionsToCascaderOptions = (regions: RegionItem[]): any[] => regions.map(region => {
  const children = convertRegionsToCascaderOptions(region.children || []);
  return {
    value: region.id,
    label: region.name,
    ...(children.length ? { children } : {}),
  };
});

const buildRegionMetaMap = (regions: RegionItem[]) => {
  const metaMap = new Map<string, { idPath: string[]; namePath: string[] }>();
  const walk = (items: RegionItem[], parentIds: string[] = [], parentNames: string[] = []) => {
    items.forEach(item => {
      const idPath = [...parentIds, item.id];
      const namePath = [...parentNames, item.name];
      metaMap.set(item.id, { idPath, namePath });
      if (item.code && item.code !== item.id) {
        metaMap.set(item.code, { idPath, namePath });
      }
      metaMap.set(namePath.join('/'), { idPath, namePath });
      if (item.children?.length) {
        walk(item.children, idPath, namePath);
      }
    });
  };
  walk(regions);
  return metaMap;
};

const parseWechatServiceRegions = (value: unknown): WechatServiceRegionValue[] => {
  if (Array.isArray(value)) {
    return value as WechatServiceRegionValue[];
  }
  if (typeof value === 'string' && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

const getWechatRegionPaths = (value: unknown, metaMap: Map<string, { idPath: string[]; namePath: string[] }>) => {
  return parseWechatServiceRegions(value)
    .map(region => {
      const regionId = String(region.regionId || '').trim();
      if (regionId && metaMap.has(regionId)) {
        return metaMap.get(regionId)?.idPath || [];
      }

      const nameKeys = [
        [region.provinceName, region.cityName, region.districtName].filter(Boolean).join('/'),
        [region.provinceName, region.cityName].filter(Boolean).join('/'),
        [region.provinceName].filter(Boolean).join('/'),
      ].filter(Boolean);

      for (const key of nameKeys) {
        const matched = metaMap.get(key);
        if (matched) return matched.idPath;
      }
      return regionId ? [regionId] : [];
    })
    .filter(path => path.length > 0);
};

const flattenCategories = (categories: ServiceCategory[], parentNames: string[] = []): { label: string; value: string }[] => {
  return categories.flatMap((category) => {
    const names = [...parentNames, category.name];
    const current = { label: names.join(' / '), value: category.id };
    return [current, ...flattenCategories(category.children || [], names)];
  });
};

const UserManagement: React.FC = () => {
  const location = useLocation();
  const isProviderPage = location.pathname === '/system/service-provider-management';
  const pageTitle = isProviderPage ? '代理商管理' : '用户管理';
  const entityName = isProviderPage ? '代理商' : '用户';
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<UserListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [modalVisible, setModalVisible] = useState(false);
  const [passwordModalVisible, setPasswordModalVisible] = useState(false);
  const [settlementModalVisible, setSettlementModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState<UserListItem | null>(null);
  const [settlementUser, setSettlementUser] = useState<UserListItem | null>(null);
  const [settlementSummary, setSettlementSummary] = useState<UserProviderSettlementSummary | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [roles, setRoles] = useState<Role[]>([]);
  const [categories, setCategories] = useState<{ label: string; value: string }[]>([]);
  const [meituanShops, setMeituanShops] = useState<MeituanShop[]>([]);
  const [regionOptions, setRegionOptions] = useState<any[]>([]);
  const [regionMetaMap, setRegionMetaMap] = useState<Map<string, { idPath: string[]; namePath: string[] }>>(new Map());
  const [uploadingField, setUploadingField] = useState<string>('');
  const [searchParams, setSearchParams] = useState<any>({});
  const [settlementLoading, setSettlementLoading] = useState(false);
  
  const [form] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [searchForm] = Form.useForm();
  const [settlementForm] = Form.useForm();
  const orderSourceWechat = Form.useWatch('orderSourceWechat', form);
  const orderSourceMeituan = Form.useWatch('orderSourceMeituan', form);
  const settlementInvoiceUrl = Form.useWatch('invoiceUrl', settlementForm);

  // 加载用户列表
  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await userService.list({ page, pageSize, ...searchParams, isProvider: isProviderPage });
      setUsers(response.users);
      setTotal(response.total);
    } catch (error) {
      message.error(`加载${entityName}列表失败`);
    } finally {
      setLoading(false);
    }
  };

  // 加载角色列表
  const loadRoles = async () => {
    try {
      const response = await roleService.list({ page: 1, pageSize: 100 });
      setRoles(response.list);
    } catch (error) {
      console.error('加载角色列表失败', error);
    }
  };

  const loadProviderOptions = async (user?: UserListItem | null) => {
    if (!isProviderPage) return;
    const [categoryRes, shopRes, regionRes] = await Promise.allSettled([
      getAllServiceCategories(),
      listMeituanShops({
          page: 1,
          pageSize: 1000,
          status: '1',
          availableForUserId: user?.id || 'new',
      }),
      getRegionTree({ status: 1 }),
    ]);

    if (categoryRes.status === 'fulfilled') {
      setCategories(flattenCategories(categoryRes.value.data || []));
    }
    if (shopRes.status === 'fulfilled') {
      setMeituanShops(shopRes.value.data?.list || []);
    }
    if (regionRes.status === 'fulfilled') {
      const regions = regionRes.value.data || [];
      const nextRegionMetaMap = buildRegionMetaMap(regions);
      setRegionOptions(convertRegionsToCascaderOptions(regions));
      setRegionMetaMap(nextRegionMetaMap);
      if (user) {
        const paths = getWechatRegionPaths(user.wechatServiceRegions, nextRegionMetaMap);
        form.setFieldsValue({ wechatRegionPaths: paths });
      }
      return;
    }

    if (categoryRes.status === 'rejected' || shopRes.status === 'rejected' || regionRes.status === 'rejected') {
      message.error('加载代理商选项失败');
    }
  };

  useEffect(() => {
    loadUsers();
  }, [page, pageSize, searchParams, isProviderPage]);

  useEffect(() => {
    loadRoles();
  }, []);

  // 打开新增/编辑对话框
  const handleOpenModal = (user?: UserListItem) => {
    if (user) {
      setEditingUser(user);
      form.setFieldsValue(user);
    } else {
      setEditingUser(null);
      form.resetFields();
    }
    if (isProviderPage) {
      form.setFieldsValue({
        orderSourceWechat: user?.orderSourceWechat ?? true,
        orderSourceMeituan: user?.orderSourceMeituan ?? false,
        wechatRegionPaths: [],
        serviceCategoryIds: user?.serviceCategoryIds || [],
        meituanShopIds: user?.meituanShopIds || [],
        settlementType: user?.settlementType || 'whole_order',
        settlementRatio: user?.settlementRatio,
        settlementAmountType: user?.settlementAmountType || 'remaining_amount_ratio',
        settlementCycleDays: user?.settlementCycleDays ?? 0,
      });
      if (user) {
        void userService.get(user.id).then(detail => {
          setEditingUser(detail);
          form.setFieldsValue({
            ...detail,
            orderSourceWechat: detail.orderSourceWechat ?? user.orderSourceWechat ?? true,
            orderSourceMeituan: detail.orderSourceMeituan ?? user.orderSourceMeituan ?? false,
            serviceCategoryIds: detail.serviceCategoryIds || user.serviceCategoryIds || [],
            meituanShopIds: detail.meituanShopIds || user.meituanShopIds || [],
            settlementType: detail.settlementType || user.settlementType || 'whole_order',
            settlementRatio: detail.settlementRatio ?? user.settlementRatio,
            settlementAmountType: detail.settlementAmountType || user.settlementAmountType || 'remaining_amount_ratio',
            settlementCycleDays: detail.settlementCycleDays ?? user.settlementCycleDays ?? 0,
          });
          return loadProviderOptions(detail);
        }).catch(() => {
          void loadProviderOptions(user);
        });
      } else {
        void loadProviderOptions(null);
      }
    }
    setModalVisible(true);
  };

  // 关闭对话框
  const handleCloseModal = () => {
    setModalVisible(false);
    setEditingUser(null);
    form.resetFields();
  };

  // 提交表单
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const wechatRegionPaths = (values.wechatRegionPaths || []) as string[][];
      const wechatServiceRegions = wechatRegionPaths
        .map(path => {
          const regionId = path[path.length - 1];
          const meta = regionMetaMap.get(regionId);
          return {
            regionId,
            provinceName: meta?.namePath[0] || '',
            cityName: meta?.namePath[1] || '',
            districtName: meta?.namePath[2] || '',
          };
        })
        .filter(item => item.regionId && item.provinceName);
      delete values.wechatRegionPaths;
      setLoading(true);

      if (editingUser) {
        await userService.update(editingUser.id, { ...values, wechatServiceRegions, isProvider: isProviderPage } as UpdateUserRequest);
        message.success(`${entityName}更新成功`);
      } else {
        await userService.create({ ...values, wechatServiceRegions, isProvider: isProviderPage } as CreateUserRequest);
        message.success(`${entityName}创建成功`);
      }
      
      handleCloseModal();
      loadUsers();
    } catch (error: any) {
      message.error(error.message || '操作失败');
    } finally {
      setLoading(false);
    }
  };

  // 删除用户
  const handleDelete = async (id: string) => {
    try {
      await userService.delete(id);
      message.success(`${entityName}删除成功`);
      loadUsers();
    } catch (error: any) {
      message.error(error.message || '删除失败');
    }
  };

  // 打开重置密码对话框
  const handleOpenPasswordModal = (userId: string) => {
    setSelectedUserId(userId);
    setPasswordModalVisible(true);
  };

  // 重置密码
  const handleResetPassword = async () => {
    try {
      const values = await passwordForm.validateFields();
      await userService.resetPassword(selectedUserId, values.newPassword);
      message.success('密码重置成功');
      setPasswordModalVisible(false);
      passwordForm.resetFields();
    } catch (error: any) {
      message.error(error.message || '密码重置失败');
    }
  };

  const loadSettlementSummary = async (userId: string) => {
    setSettlementLoading(true);
    try {
      const summary = await userService.getProviderSettlementFlows(userId);
      setSettlementSummary(summary);
      setSettlementUser(summary.provider);
    } catch (error: any) {
      message.error(error.message || '加载结算流水失败');
    } finally {
      setSettlementLoading(false);
    }
  };

  const handleOpenSettlementModal = (user: UserListItem) => {
    setSettlementUser(user);
    setSettlementSummary(null);
    settlementForm.resetFields();
    setSettlementModalVisible(true);
    void loadSettlementSummary(user.id);
  };

  const handleCloseSettlementModal = () => {
    setSettlementModalVisible(false);
    setSettlementUser(null);
    setSettlementSummary(null);
    settlementForm.resetFields();
  };

  const handleCreateSettlement = async () => {
    if (!settlementUser) return;
    try {
      const values = await settlementForm.validateFields();
      setSettlementLoading(true);
      const summary = await userService.createProviderSettlementFlow(settlementUser.id, values);
      setSettlementSummary(summary);
      setSettlementUser(summary.provider);
      settlementForm.resetFields();
      message.success('结算成功');
      void loadUsers();
    } catch (error: any) {
      message.error(error.message || '结算失败');
    } finally {
      setSettlementLoading(false);
    }
  };

  // 搜索
  const handleSearch = () => {
    const values = searchForm.getFieldsValue();
    setSearchParams(values);
    setPage(1);
  };

  // 重置搜索
  const handleResetSearch = () => {
    searchForm.resetFields();
    setSearchParams({});
    setPage(1);
  };

  const createUploadHandler = (fieldName: 'businessLicense' | 'idCardFront' | 'idCardBack'): UploadProps['customRequest'] => {
    return async (options) => {
      const { file, onSuccess, onError } = options;
      setUploadingField(fieldName);
      try {
        const res = await uploadFile(file as File, 'service_provider_cert');
        form.setFieldsValue({ [fieldName]: res.data.url });
        message.success('上传成功');
        onSuccess?.(res.data);
      } catch (error: any) {
        message.error(error?.response?.data?.message || '上传失败');
        onError?.(error);
      } finally {
        setUploadingField('');
      }
    };
  };

  const handleSettlementInvoiceUpload: UploadProps['customRequest'] = async (options) => {
    const { file, onSuccess, onError } = options;
    setUploadingField('settlementInvoiceUrl');
    try {
      const res = await uploadFile(file as File, 'provider_settlement_invoice');
      settlementForm.setFieldsValue({ invoiceUrl: res.data.url });
      message.success('上传成功');
      onSuccess?.(res.data);
    } catch (error: any) {
      message.error(error?.response?.data?.message || '上传失败');
      onError?.(error);
    } finally {
      setUploadingField('');
    }
  };

  const renderImageUpload = (fieldName: 'businessLicense' | 'idCardFront' | 'idCardBack', label: string) => {
    const value = form.getFieldValue(fieldName);
    return (
      <Form.Item name={fieldName} label={label}>
        <Space align="start">
          <Upload accept="image/*" showUploadList={false} customRequest={createUploadHandler(fieldName)}>
            <Button loading={uploadingField === fieldName} icon={<UploadOutlined />}>上传{label}</Button>
          </Upload>
          {value ? <Image src={normalizeFileUrl(value)} width={80} height={80} style={{ objectFit: 'cover', borderRadius: 8 }} /> : null}
        </Space>
      </Form.Item>
    );
  };

  const getRoleLabel = (roleCode: string) => {
    const role = roles.find(item => item.code === roleCode || item.id === roleCode);
    if (role) return role.name;
    const roleMap: { [key: string]: string } = {
      disease_control: '疾控中心管理员',
      school_medical: '学校医务室',
      community: '社区检查点',
      doctor: '医生',
    };
    return roleMap[roleCode] || roleCode;
  };

  const isSuperAdminUser = (user?: UserListItem | null) => {
    return String(user?.username || '').trim().toLowerCase() === 'admin';
    /*
    return roleCode === 'super_admin' || roleCode === 'admin' || roleName === '超级管理员';
    */
  };

  const settlementFlowColumns = [
    {
      title: '结算金额',
      dataIndex: 'amount',
      key: 'amount',
      width: 110,
      render: (amount: number) => `¥${Number(amount || 0).toFixed(2)}`,
    },
    {
      title: '结算前',
      dataIndex: 'beforeAmount',
      key: 'beforeAmount',
      width: 110,
      render: (amount: number) => `¥${Number(amount || 0).toFixed(2)}`,
    },
    {
      title: '结算后',
      dataIndex: 'afterAmount',
      key: 'afterAmount',
      width: 110,
      render: (amount: number) => `¥${Number(amount || 0).toFixed(2)}`,
    },
    {
      title: '操作人员',
      dataIndex: 'operatorName',
      key: 'operatorName',
      width: 120,
    },
    {
      title: '备注',
      dataIndex: 'remark',
      key: 'remark',
      ellipsis: true,
    },
    {
      title: '发票',
      dataIndex: 'invoiceUrl',
      key: 'invoiceUrl',
      width: 90,
      render: (url: string) => url ? <a href={normalizeFileUrl(url)} target="_blank" rel="noreferrer">查看</a> : '-',
    },
    {
      title: '结算时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 170,
    },
  ];

  const columns = [
    {
      title: '用户名',
      dataIndex: 'username',
      key: 'username',
      width: 120,
    },
    {
      title: '姓名',
      dataIndex: 'name',
      key: 'name',
      width: 100,
    },
    {
      title: '角色',
      dataIndex: 'role',
      key: 'role',
      width: 150,
      render: (role: string) => {
        const colorMap: { [key: string]: string } = {
          disease_control: 'blue',
          school_medical: 'green',
          community: 'orange',
          doctor: 'purple',
        };
        return <Tag color={colorMap[role] || 'default'}>{getRoleLabel(role)}</Tag>;
      },
    },
    ...(!isProviderPage ? [{
      title: '所属机构',
      dataIndex: 'organization',
      key: 'organization',
      width: 150,
    }] : []),
    {
      title: '联系电话',
      dataIndex: 'phone',
      key: 'phone',
      width: 120,
    },
    ...(isProviderPage ? [{
      title: '可提现金额',
      dataIndex: 'withdrawableAmount',
      key: 'withdrawableAmount',
      width: 120,
      render: (amount: number) => `¥${Number(amount || 0).toFixed(2)}`,
    }] : []),
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (status: string) => (
        <Tag color={status === 'active' ? 'success' : 'default'}>
          {status === 'active' ? '正常' : '禁用'}
        </Tag>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
    },
    {
      title: '操作',
      key: 'action',
      width: isProviderPage ? 300 : 220,
      fixed: 'right' as const,
      render: (_: any, record: UserListItem) => {
        const isSuperAdmin = isSuperAdminUser(record);

        return (
          <Space size="small">
            {isProviderPage && (
              <Button
                type="link"
                size="small"
                onClick={() => handleOpenSettlementModal(record)}
              >
                结算
              </Button>
            )}
            {!isSuperAdmin && (
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleOpenModal(record)}
          >
            编辑
          </Button>
            )}
          <Button
            type="link"
            size="small"
            icon={<KeyOutlined />}
            onClick={() => handleOpenPasswordModal(record.id)}
          >
            重置密码
          </Button>
            {!isSuperAdmin && (
          <Popconfirm
            title={`确定要删除这个${entityName}吗？`}
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
            >
              删除
            </Button>
          </Popconfirm>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <div>
      <Card style={{ marginBottom: 16 }}>
        <Form form={searchForm} layout="inline">
          <Form.Item name="username" label="用户名">
            <Input placeholder="请输入用户名" style={{ width: 150 }} />
          </Form.Item>
          <Form.Item name="name" label="姓名">
            <Input placeholder="请输入姓名" style={{ width: 150 }} />
          </Form.Item>
          <Form.Item name="role" label="角色">
            <Select placeholder="请选择角色" style={{ width: 150 }} allowClear>
              {roles.map(role => (
                <Option key={role.code} value={role.code}>{role.name}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="status" label="状态">
            <Select placeholder="请选择状态" style={{ width: 120 }} allowClear>
              <Option value="active">正常</Option>
              <Option value="inactive">禁用</Option>
            </Select>
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
                搜索
              </Button>
              <Button onClick={handleResetSearch}>重置</Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      <Card
        title={<Title level={4} style={{ margin: 0 }}>{pageTitle}</Title>}
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => handleOpenModal()}>
            新增{entityName}
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={users}
          rowKey="id"
          loading={loading}
          pagination={{
            current: page,
            pageSize: pageSize,
            total: total,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条记录`,
            onChange: (page, pageSize) => {
              setPage(page);
              setPageSize(pageSize);
            },
          }}
        />
      </Card>

      {/* 新增/编辑用户对话框 */}
      <Modal
        title={editingUser ? `编辑${entityName}` : `新增${entityName}`}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={handleCloseModal}
        width={760}
        confirmLoading={loading}
      >
        <Form form={form} layout="vertical" autoComplete="off">
          <Row gutter={16}>
            <Col span={isProviderPage && !editingUser ? 12 : 24}>
              <Form.Item
                name="username"
                label="用户名"
                rules={[
                  { required: true, message: '请输入用户名' },
                  { min: 3, message: '用户名至少3个字符' },
                ]}
              >
                <Input placeholder="请输入用户名" disabled={!!editingUser} autoComplete="new-username" />
              </Form.Item>
            </Col>
            {isProviderPage && !editingUser && (
              <Col span={12}>
                <Form.Item
                  name="password"
                  label="密码"
                  rules={[
                    { required: true, message: '请输入密码' },
                    { min: 6, message: '密码至少6个字符' },
                  ]}
                >
                  <Input.Password placeholder="请输入密码" autoComplete="new-password" />
                </Form.Item>
              </Col>
            )}
          </Row>

          {!isProviderPage && !editingUser && (
            <Form.Item
              name="password"
              label="密码"
              rules={[
                { required: true, message: '请输入密码' },
                { min: 6, message: '密码至少6个字符' },
              ]}
            >
              <Input.Password placeholder="请输入密码" autoComplete="new-password" />
            </Form.Item>
          )}

          <Row gutter={16}>
            <Col span={isProviderPage ? 12 : 24}>
              <Form.Item
                name="name"
                label="姓名"
                rules={[{ required: true, message: '请输入姓名' }]}
              >
                <Input placeholder="请输入姓名" />
              </Form.Item>
            </Col>
            {isProviderPage && (
              <Col span={12}>
                <Form.Item name="phone" label="联系电话">
                  <Input placeholder="请输入联系电话" />
                </Form.Item>
              </Col>
            )}
          </Row>

          <Row gutter={16}>
            <Col span={isProviderPage ? 12 : 24}>
              <Form.Item
                name="role"
                label="角色"
                rules={[{ required: true, message: '请选择角色' }]}
              >
                <Select placeholder="请选择角色">
                  {roles.map(role => (
                    <Option key={role.code} value={role.code}>{role.name}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            {isProviderPage && (
              <Col span={12}>
                <Form.Item name="serviceCategoryIds" label="接单服务分类" rules={[{ required: true, message: '请选择接单服务分类' }]}>
                  <Select mode="multiple" placeholder="请选择服务分类" options={categories} showSearch optionFilterProp="label" />
                </Form.Item>
              </Col>
            )}
          </Row>

          {!isProviderPage && (
            <Form.Item name="organization" label="所属机构">
              <Input placeholder="请输入所属机构" />
            </Form.Item>
          )}

          {!isProviderPage && (
            <Form.Item name="phone" label="联系电话">
              <Input placeholder="请输入联系电话" />
            </Form.Item>
          )}

          <Form.Item name="email" label="邮箱" rules={[{ type: 'email', message: '请输入有效的邮箱' }]}>
            <Input placeholder="请输入邮箱" />
          </Form.Item>

          {isProviderPage && (
            <>
              <Row gutter={16}>
                <Col span={8}>
                  {renderImageUpload('businessLicense', '营业执照')}
                </Col>
                <Col span={8}>
                  {renderImageUpload('idCardFront', '身份证正面')}
                </Col>
                <Col span={8}>
                  {renderImageUpload('idCardBack', '身份证反面')}
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item
                    name="settlementType"
                    label="结算类型"
                    rules={[{ required: true, message: '请选择结算类型' }]}
                  >
                    <Radio.Group>
                      <Radio value="whole_order">整单结算</Radio>
                      <Radio value="sub_order">子订单结算</Radio>
                    </Radio.Group>
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="settlementRatio"
                    label="结算比例"
                    rules={[{ required: true, message: '请输入结算比例' }]}
                  >
                    <InputNumber min={0} max={100} precision={2} addonAfter="%" style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="settlementAmountType"
                    label="金额类型"
                    rules={[{ required: true, message: '请选择金额类型' }]}
                  >
                    <Radio.Group>
                      <Radio value="remaining_amount_ratio">剩余金额比例</Radio>
                      <Radio value="paid_amount_ratio">支付金额比例</Radio>
                    </Radio.Group>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item
                    name="settlementCycleDays"
                    label="结算周期"
                    rules={[{ required: true, message: '请输入结算周期' }]}
                    extra="0代表立即结算，大于0则生成待结算流水"
                  >
                    <InputNumber min={0} precision={0} addonAfter="天" style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item label="订单来源" required>
                <Space orientation="vertical" size={12} style={{ width: '100%' }}>
                  <Form.Item name="orderSourceWechat" valuePropName="checked" noStyle>
                    <Checkbox>微信</Checkbox>
                  </Form.Item>
                  {orderSourceWechat && (
                    <Form.Item name="wechatRegionPaths" label="绑定微信服务城市" rules={[{ required: true, message: '请选择微信服务城市' }]}>
                      <Cascader
                        multiple
                        options={regionOptions}
                        placeholder="请选择微信服务城市"
                        changeOnSelect
                        showCheckedStrategy={Cascader.SHOW_PARENT}
                        style={{ width: '100%' }}
                      />
                    </Form.Item>
                  )}
                  <Form.Item name="orderSourceMeituan" valuePropName="checked" noStyle>
                    <Checkbox>美团</Checkbox>
                  </Form.Item>
                </Space>
              </Form.Item>

              {orderSourceMeituan && (
                <Form.Item name="meituanShopIds" label="绑定美团门店" rules={[{ required: true, message: '请选择美团门店' }]}>
                  <Select
                    mode="multiple"
                    placeholder="只显示未绑定其他代理商的门店"
                    showSearch
                    optionFilterProp="label"
                    options={meituanShops.map(shop => ({
                      label: `${shop.shopName || shop.opBizName || shop.shopId}（${shop.shopId}）`,
                      value: shop.id,
                    }))}
                  />
                </Form.Item>
              )}
            </>
          )}

          {editingUser && (
            <Form.Item name="status" label="状态">
              <Select>
                <Option value="active">正常</Option>
                <Option value="inactive">禁用</Option>
              </Select>
            </Form.Item>
          )}
        </Form>
      </Modal>

      <Modal
        title="代理商结算"
        open={settlementModalVisible}
        onCancel={handleCloseSettlementModal}
        footer={null}
        width={980}
        destroyOnClose
      >
        <Space orientation="vertical" size={16} style={{ width: '100%' }}>
          <Descriptions bordered column={2} size="small">
            <Descriptions.Item label="用户名">{settlementUser?.username || '-'}</Descriptions.Item>
            <Descriptions.Item label="姓名">{settlementUser?.name || '-'}</Descriptions.Item>
            <Descriptions.Item label="电话">{settlementUser?.phone || '-'}</Descriptions.Item>
            <Descriptions.Item label="可提现金额">
              ¥{Number((settlementSummary?.provider || settlementUser)?.withdrawableAmount || 0).toFixed(2)}
            </Descriptions.Item>
          </Descriptions>

          <Card title="新增结算流水" size="small">
            <Form form={settlementForm} layout="vertical" autoComplete="off">
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item
                    name="amount"
                    label="结算金额"
                    rules={[{ required: true, message: '请输入结算金额' }]}
                  >
                    <InputNumber min={0.01} precision={2} style={{ width: '100%' }} placeholder="请输入结算金额" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="operatorName" label="操作人员">
                    <Input placeholder="不填默认当前登录人员" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="invoiceUrl" label="结算发票">
                    <Space>
                      <Upload showUploadList={false} customRequest={handleSettlementInvoiceUpload}>
                        <Button loading={uploadingField === 'settlementInvoiceUrl'} icon={<UploadOutlined />}>上传发票</Button>
                      </Upload>
                      {settlementInvoiceUrl ? <a href={normalizeFileUrl(settlementInvoiceUrl)} target="_blank" rel="noreferrer">查看</a> : null}
                    </Space>
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item name="remark" label="结算备注">
                <Input.TextArea rows={3} placeholder="请输入结算备注" />
              </Form.Item>
              <Button type="primary" loading={settlementLoading} onClick={handleCreateSettlement}>
                结算
              </Button>
            </Form>
          </Card>

          <Card title="结算流水" size="small">
            <Table
              columns={settlementFlowColumns}
              dataSource={settlementSummary?.flows || []}
              rowKey="id"
              loading={settlementLoading}
              pagination={{ pageSize: 5 }}
              scroll={{ x: 860 }}
            />
          </Card>
        </Space>
      </Modal>

      {/* 重置密码对话框 */}
      <Modal
        title="重置密码"
        open={passwordModalVisible}
        onOk={handleResetPassword}
        onCancel={() => {
          setPasswordModalVisible(false);
          passwordForm.resetFields();
        }}
      >
        <Form form={passwordForm} layout="vertical">
          <Form.Item
            name="newPassword"
            label="新密码"
            rules={[
              { required: true, message: '请输入新密码' },
              { min: 6, message: '密码至少6个字符' },
            ]}
          >
            <Input.Password placeholder="请输入新密码" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UserManagement;
